import type { AgeGroup, Language } from '../../types'

// ─── Tile types ───────────────────────────────────────────────────────────────

export type PuzzleTile =
  | { kind: 'image';   emoji: string; word: string }   // emoji + word it represents
  | { kind: 'op';      symbol: '+' | '-' }
  | { kind: 'letters'; text: string }                  // letters to add or remove

export interface StaticRebusPuzzle {
  tiles:  PuzzleTile[]
  answer: string    // UPPERCASE
  hint:   string    // describes the answer
}

// ─── Shorthand helpers ────────────────────────────────────────────────────────

const img = (emoji: string, word: string): PuzzleTile => ({ kind: 'image', emoji, word })
const op  = (symbol: '+' | '-'): PuzzleTile           => ({ kind: 'op', symbol })
const ltr = (text: string): PuzzleTile                => ({ kind: 'letters', text })

/** image + letters  (most common form) */
const iplus = (emoji: string, word: string, text: string): PuzzleTile[] =>
  [img(emoji, word), op('+'), ltr(text)]

// ─── English ──────────────────────────────────────────────────────────────────

const EN_TINY: StaticRebusPuzzle[] = [
  { tiles: [img('🐱', 'CAT')],    answer: 'CAT',      hint: 'A soft pet that purrs' },
  { tiles: [img('🐶', 'DOG')],    answer: 'DOG',      hint: 'A loyal pet that barks' },
  { tiles: [img('☀️', 'SUN')],    answer: 'SUN',      hint: 'It warms us from the sky' },
  { tiles: [img('🌙', 'MOON')],   answer: 'MOON',     hint: 'It glows in the night sky' },
  { tiles: [img('⭐', 'STAR')],   answer: 'STAR',     hint: 'It twinkles high above' },
  { tiles: [img('🐸', 'FROG')],   answer: 'FROG',     hint: 'A green jumper near ponds' },
  { tiles: [img('🍎', 'APPLE')],  answer: 'APPLE',    hint: 'A crisp red or green fruit' },
  { tiles: [img('🐠', 'FISH')],   answer: 'FISH',     hint: 'It swims with fins and scales' },
  { tiles: [img('🌳', 'TREE')],   answer: 'TREE',     hint: 'A tall plant with a trunk' },
  { tiles: [img('🐻', 'BEAR')],   answer: 'BEAR',     hint: 'A big furry hibernating animal' },
  { tiles: [img('🐝', 'BEE')],    answer: 'BEE',      hint: 'A stripy insect that makes honey' },
  { tiles: [img('🦉', 'OWL')],    answer: 'OWL',      hint: 'A night bird that hoots' },
  { tiles: [img('🐷', 'PIG')],    answer: 'PIG',      hint: 'A pink oinking farm animal' },
  { tiles: [img('🥚', 'EGG')],    answer: 'EGG',      hint: 'A bird hatches from this' },
  { tiles: [img('🐜', 'ANT')],    answer: 'ANT',      hint: 'A tiny insect in a colony' },
  { tiles: [img('🐌', 'SNAIL')],  answer: 'SNAIL',    hint: 'It carries its house on its back' },
  { tiles: [img('🦊', 'FOX')],    answer: 'FOX',      hint: 'A clever animal with a bushy tail' },
  { tiles: [img('🐇', 'RABBIT')], answer: 'RABBIT',   hint: 'It has long ears and hops fast' },
]

const EN_YOUNG: StaticRebusPuzzle[] = [
  {
    tiles:  [...iplus('⭐', 'STAR', 'T')],
    answer: 'START',
    hint:   'To begin something',
  },
  {
    tiles:  [...iplus('🌊', 'SEA', 'SON')],
    answer: 'SEASON',
    hint:   'Spring, summer, autumn, or winter',
  },
  {
    tiles:  [...iplus('🌊', 'SEA', 'BIRD')],
    answer: 'SEABIRD',
    hint:   'A bird that lives near the ocean',
  },
  {
    tiles:  [...iplus('❄️', 'ICE', 'BERG')],
    answer: 'ICEBERG',
    hint:   'A huge floating block of ice',
  },
  {
    tiles:  [...iplus('☀️', 'SUN', 'SHINE')],
    answer: 'SUNSHINE',
    hint:   'Bright light from the sun',
  },
  {
    tiles:  [...iplus('🌙', 'MOON', 'LIGHT')],
    answer: 'MOONLIGHT',
    hint:   'The soft glow of the moon at night',
  },
  {
    tiles:  [...iplus('🐝', 'BEE', 'HIVE')],
    answer: 'BEEHIVE',
    hint:   'Where bees live and store honey',
  },
  {
    tiles:  [...iplus('🌹', 'ROSE', 'BUD')],
    answer: 'ROSEBUD',
    hint:   'A rose that has not yet opened',
  },
  {
    tiles:  [img('⭐', 'STAR'), op('+'), img('🐠', 'FISH')],
    answer: 'STARFISH',
    hint:   'A star-shaped sea creature',
  },
  {
    tiles:  [...iplus('☀️', 'SUN', 'DAY')],
    answer: 'SUNDAY',
    hint:   'The last day of the weekend',
  },
  {
    tiles:  [...iplus('🌊', 'SEA', 'WEED')],
    answer: 'SEAWEED',
    hint:   'A plant that grows in the sea',
  },
  {
    tiles:  [...iplus('🐦', 'BIRD', 'CAGE')],
    answer: 'BIRDCAGE',
    hint:   'A box where pet birds live',
  },
  {
    tiles:  [...iplus('🌳', 'TREE', 'TOP')],
    answer: 'TREETOP',
    hint:   'The highest branches of a tree',
  },
  {
    tiles:  [img('🌊', 'SEA'), op('+'), img('🐚', 'SHELL')],
    answer: 'SEASHELL',
    hint:   'A hard case washed up on the beach',
  },
]

const EN_MIDDLE: StaticRebusPuzzle[] = [
  {
    tiles:  [...iplus('🌹', 'ROSE', 'MARY')],
    answer: 'ROSEMARY',
    hint:   'A fragrant herb used in cooking',
  },
  {
    tiles:  [...iplus('🌊', 'SEA', 'SIDE')],
    answer: 'SEASIDE',
    hint:   'The area along the coast',
  },
  {
    tiles:  [...iplus('☀️', 'SUN', 'BURN')],
    answer: 'SUNBURN',
    hint:   'Red skin from too much sun',
  },
  {
    tiles:  [...iplus('🏠', 'HOUSE', 'HOLD')],
    answer: 'HOUSEHOLD',
    hint:   'All the people living in one home',
  },
  {
    tiles:  [...iplus('❄️', 'ICE', 'LAND')],
    answer: 'ICELAND',
    hint:   'A Nordic island nation',
  },
  {
    tiles:  [...iplus('🌙', 'MOON', 'SHINE')],
    answer: 'MOONSHINE',
    hint:   'Moonlight; or secretly made drink',
  },
  {
    tiles:  [...iplus('⭐', 'STAR', 'BOARD')],
    answer: 'STARBOARD',
    hint:   'The right side of a ship',
  },
  {
    tiles:  [...iplus('🌹', 'ROSE', 'WOOD')],
    answer: 'ROSEWOOD',
    hint:   'A dark-coloured tropical hardwood',
  },
  {
    tiles:  [...iplus('🌙', 'MOON', 'BEAM')],
    answer: 'MOONBEAM',
    hint:   'A ray of light from the moon',
  },
  {
    tiles:  [...iplus('🌙', 'MOON', 'FLOWER')],
    answer: 'MOONFLOWER',
    hint:   'A white flower that blooms at night',
  },
  {
    tiles:  [...iplus('🌊', 'SEA', 'WORTHY')],
    answer: 'SEAWORTHY',
    hint:   'Safe to sail on the open sea',
  },
  {
    tiles:  [...iplus('⭐', 'STAR', 'LIGHT')],
    answer: 'STARLIGHT',
    hint:   'The light that comes from the stars',
  },
]

// ─── Slovenian ────────────────────────────────────────────────────────────────

const SL_TINY: StaticRebusPuzzle[] = [
  { tiles: [img('🐱', 'MAČKA')],  answer: 'MAČKA',   hint: 'Domača živalca, ki prede in mijavka' },
  { tiles: [img('🐶', 'PES')],    answer: 'PES',     hint: 'Zvest hišni ljubimec, ki laja' },
  { tiles: [img('☀️', 'SONCE')],  answer: 'SONCE',   hint: 'Naša zvezda, ki sveti podnevi' },
  { tiles: [img('🌙', 'LUNA')],   answer: 'LUNA',    hint: 'Naravni satelit Zemlje' },
  { tiles: [img('🐸', 'ŽABA')],   answer: 'ŽABA',    hint: 'Zelena živalca, ki skače in reglja' },
  { tiles: [img('🍎', 'JABOLKO')],answer: 'JABOLKO', hint: 'Sočno rdeče ali zeleno sadje' },
  { tiles: [img('🌊', 'MORJE')],  answer: 'MORJE',   hint: 'Velika slana voda ob obali' },
  { tiles: [img('🌳', 'DREVO')],  answer: 'DREVO',   hint: 'Visoka rastlina z deblom in vejami' },
  { tiles: [img('🐻', 'MEDVED')], answer: 'MEDVED',  hint: 'Velika dlakava žival, ki prezimi' },
  { tiles: [img('🐝', 'ČEBELA')], answer: 'ČEBELA',  hint: 'Rumeno-črni insekt, ki dela med' },
  { tiles: [img('🌸', 'CVET')],   answer: 'CVET',    hint: 'Barven del rastline' },
  { tiles: [img('⭐', 'ZVEZDA')],  answer: 'ZVEZDA',  hint: 'Sveti na nočnem nebu' },
  { tiles: [img('🦊', 'LISICA')], answer: 'LISICA',  hint: 'Zvita rdeča žival z bushnato repom' },
  { tiles: [img('🐺', 'VOLK')],   answer: 'VOLK',    hint: 'Divja žival, ki tuli v tropih' },
  { tiles: [img('🐇', 'ZAJEC')],  answer: 'ZAJEC',   hint: 'Majhna živalca z dolgimi ušesi' },
]

const SL_YOUNG: StaticRebusPuzzle[] = [
  {
    tiles:  [...iplus('🌲', 'GOZD', 'AR')],
    answer: 'GOZDAR',
    hint:   'Oseba, ki skrbi za gozd in živali v njem',
  },
  {
    tiles:  [...iplus('🌳', 'LES', 'EN')],
    answer: 'LESEN',
    hint:   'Narejen iz lesa',
  },
  {
    tiles:  [img('🐟', 'RIBA'), op('-'), ltr('A'), op('+'), ltr('AR')],
    answer: 'RIBAR',
    hint:   'Oseba, ki lovi ribe',
  },
  {
    tiles:  [img('🏔️', 'GORA'), op('-'), ltr('A'), op('+'), ltr('NIK')],
    answer: 'GORNIK',
    hint:   'Oseba, ki rada hodi v gore',
  },
  {
    tiles:  [...iplus('🌸', 'CVET', 'LICA')],
    answer: 'CVETLICA',
    hint:   'Rastlina z lepimi barvnimi cvetovi',
  },
  {
    tiles:  [img('🐝', 'ČEBELA'), op('-'), ltr('A'), op('+'), ltr('NJAK')],
    answer: 'ČEBELNJAK',
    hint:   'Kraj, kjer čebele živijo in delajo med',
  },
  {
    tiles:  [...iplus('🐶', 'PES', 'EK')],
    answer: 'PESEK',
    hint:   'Drobna zrna kamnin na plaži ali v peskovniku',
  },
  {
    tiles:  [img('⭐', 'ZVEZDA'), op('-'), ltr('A'), op('+'), ltr('AR')],
    answer: 'ZVEZDAR',
    hint:   'Oseba, ki opazuje zvezde in planete',
  },
  {
    tiles:  [...iplus('🐻', 'MEDVED', 'KA')],
    answer: 'MEDVEDKA',
    hint:   'Samica medveda',
  },
  {
    tiles:  [img('🌊', 'MORJE'), op('-'), ltr('JE'), op('+'), ltr('NAR')],
    answer: 'MORNAR',
    hint:   'Oseba, ki dela na ladji in pluje po morju',
  },
  {
    tiles:  [img('🌹', 'ROŽA'), op('-'), ltr('A'), op('+'), ltr('NICA')],
    answer: 'ROŽNICA',
    hint:   'Prozorna plast na sprednjem delu očesa',
  },
  {
    tiles:  [...iplus('🌲', 'GOZD', 'EK')],
    answer: 'GOZDEK',
    hint:   'Manjši gozd',
  },
]

const SL_MIDDLE: StaticRebusPuzzle[] = [
  {
    tiles:  [...iplus('🌲', 'GOZD', 'ARSTVO')],
    answer: 'GOZDARSTVO',
    hint:   'Veda o gozdu, negi dreves in upravljanju z gozdovi',
  },
  {
    tiles:  [img('🐝', 'ČEBELA'), op('-'), ltr('A'), op('+'), ltr('ARSTVO')],
    answer: 'ČEBELARSTVO',
    hint:   'Vzreja čebel in pridobivanje medu',
  },
  {
    tiles:  [img('⭐', 'ZVEZDA'), op('-'), ltr('A'), op('+'), ltr('ARSTVO')],
    answer: 'ZVEZDARSTVO',
    hint:   'Veda o zvezdah, planetih in vesolju',
  },
  {
    tiles:  [img('🌊', 'MORJE'), op('-'), ltr('JE'), op('+'), ltr('NARSTVO')],
    answer: 'MORNARSTVO',
    hint:   'Ladjarstvo; plovba po morju in oceanih',
  },
  {
    tiles:  [img('🏔️', 'GORA'), op('-'), ltr('A'), op('+'), ltr('NIŠTVO')],
    answer: 'GORNIŠTVO',
    hint:   'Planinstvo; hoja in plezanje v gorah',
  },
  {
    tiles:  [...iplus('🌳', 'LES', 'ARSTVO')],
    answer: 'LESARSTVO',
    hint:   'Obrt rezanja in obdelave lesa',
  },
  {
    tiles:  [img('🐟', 'RIBA'), op('-'), ltr('A'), op('+'), ltr('ARSTVO')],
    answer: 'RIBARSTVO',
    hint:   'Pridobivanje rib; ribolov v velikem obsegu',
  },
  {
    tiles:  [...iplus('🐶', 'PES', 'NIŠTVO')],
    answer: 'PESNIŠTVO',
    hint:   'Umetnost pisanja pesmi; poezija',
  },
  {
    tiles:  [img('🌹', 'ROŽA'), op('-'), ltr('A'), op('+'), ltr('MARIN')],
    answer: 'ROŽMARIN',
    hint:   'Dišeča zelišča, ki se pogosto uporablja v kuhinji',
  },
  {
    tiles:  [img('🐻', 'MEDVED'), op('+'), ltr('NJAK')],
    answer: 'MEDVEDNJAK',
    hint:   'Tradicionalna slovenska medena peciva',
  },
]

// ─── Italian ──────────────────────────────────────────────────────────────────

const IT_TINY: StaticRebusPuzzle[] = [
  { tiles: [img('🐱', 'GATTO')],  answer: 'GATTO',   hint: 'Animale domestico che fa le fusa' },
  { tiles: [img('🐶', 'CANE')],   answer: 'CANE',    hint: 'Fedele amico dell\'uomo che abbaia' },
  { tiles: [img('☀️', 'SOLE')],   answer: 'SOLE',    hint: 'La nostra stella che scalda la Terra' },
  { tiles: [img('🌙', 'LUNA')],   answer: 'LUNA',    hint: 'Il satellite naturale della Terra' },
  { tiles: [img('🐸', 'RANA')],   answer: 'RANA',    hint: 'Anfibio verde che gracchia' },
  { tiles: [img('🍎', 'MELA')],   answer: 'MELA',    hint: 'Frutto tondo rosso o verde' },
  { tiles: [img('🌊', 'MARE')],   answer: 'MARE',    hint: 'Grande distesa di acqua salata' },
  { tiles: [img('🌳', 'ALBERO')], answer: 'ALBERO',  hint: 'Pianta con tronco, rami e foglie' },
  { tiles: [img('🐻', 'ORSO')],   answer: 'ORSO',    hint: 'Grande animale peloso che va in letargo' },
  { tiles: [img('🐝', 'APE')],    answer: 'APE',     hint: 'Insetto che produce il miele' },
  { tiles: [img('🌸', 'FIORE')],  answer: 'FIORE',   hint: 'La parte colorata della pianta' },
  { tiles: [img('⭐', 'STELLA')],  answer: 'STELLA',  hint: 'Brilla di notte nel cielo' },
  { tiles: [img('🦊', 'VOLPE')],  answer: 'VOLPE',   hint: 'Animale astuto con la coda folta' },
  { tiles: [img('🐺', 'LUPO')],   answer: 'LUPO',    hint: 'Animale selvatico che ulula alla luna' },
  { tiles: [img('🐇', 'CONIGLIO')],answer:'CONIGLIO',hint: 'Animale con le orecchie lunghe che salta' },
]

const IT_YOUNG: StaticRebusPuzzle[] = [
  {
    tiles:  [img('🐟', 'PESCE'), op('+'), img('🐶', 'CANE')],
    answer: 'PESCECANE',
    hint:   'Il nome italiano per lo squalo',
  },
  {
    tiles:  [img('🥦', 'CAVOLO'), op('-'), ltr('O'), op('+'), img('🌸', 'FIORE')],
    answer: 'CAVOLFIORE',
    hint:   'Verdura bianca simile ai broccoli',
  },
  {
    tiles:  [...iplus('🌹', 'ROSA', 'RIO')],
    answer: 'ROSARIO',
    hint:   'Collana di perle usata per pregare',
  },
  {
    tiles:  [img('🌍', 'TERRA'), op('+'), ltr('ZZO')],
    answer: 'TERRAZZO',
    hint:   'Pavimentazione decorativa; balcone esterno',
  },
  {
    tiles:  [img('🌊', 'MARE'), op('+'), ltr('MOTO')],
    answer: 'MAREMOTO',
    hint:   'Sisma sottomarino che crea onde giganti',
  },
  {
    tiles:  [...iplus('🏠', 'CASA', 'LINGA')],
    answer: 'CASALINGA',
    hint:   'Persona che si occupa della casa',
  },
  {
    tiles:  [img('🌹', 'ROSA'), op('+'), ltr('TO')],
    answer: 'ROSATO',
    hint:   'Di colore rosa; tipo di vino',
  },
  {
    tiles:  [img('🌺', 'FIORE'), op('-'), ltr('E'), op('+'), ltr('AIO')],
    answer: 'FIORAIO',
    hint:   'Persona che vende fiori',
  },
  {
    tiles:  [img('🌳', 'BOSCO'), op('-'), ltr('O'), op('+'), ltr('AIOLO')],
    answer: 'BOSCAIOLO',
    hint:   'Chi lavora nel bosco tagliando alberi',
  },
  {
    tiles:  [...iplus('🐝', 'APE', 'RTURA')],
    answer: 'APERTURA',
    hint:   'Un\'apertura o un inizio',
  },
]

const IT_MIDDLE: StaticRebusPuzzle[] = [
  {
    tiles:  [img('🌍', 'TERRA'), op('+'), ltr('FERMA')],
    answer: 'TERRAFERMA',
    hint:   'La terraferma; il continente (non il mare)',
  },
  {
    tiles:  [img('🌙', 'LUNA'), op('+'), ltr('TICO')],
    answer: 'LUNATICO',
    hint:   'Chi cambia umore improvvisamente',
  },
  {
    tiles:  [img('🌍', 'TERRA'), op('+'), ltr('COTTA')],
    answer: 'TERRACOTTA',
    hint:   'Argilla cotta usata per vasi e tegole',
  },
  {
    tiles:  [img('🌺', 'FIORE'), op('+'), ltr('NTINO')],
    answer: 'FIORENTINO',
    hint:   'Di Firenze; tipico della città di Firenze',
  },
  {
    tiles:  [...iplus('☀️', 'SOLE', 'NNE')],
    answer: 'SOLENNE',
    hint:   'Serio e formale; pieno di solennità',
  },
  {
    tiles:  [img('🌙', 'LUNA'), op('+'), ltr('PARK')],
    answer: 'LUNAPARK',
    hint:   'Parco dei divertimenti con giostre e attrazioni',
  },
  {
    tiles:  [img('🌊', 'MARE'), op('+'), ltr('SCIALLO')],
    answer: 'MARESCIALLO',
    hint:   'Alto grado militare; ufficiale delle forze armate',
  },
  {
    tiles:  [img('⭐', 'STELLA'), op('-'), ltr('A'), op('+'), ltr('ARE')],
    answer: 'STELLARE',
    hint:   'Relativo alle stelle; straordinario',
  },
]

// ─── Lookup ───────────────────────────────────────────────────────────────────

const BANKS: Record<Language, Record<AgeGroup, StaticRebusPuzzle[]>> = {
  en: { tiny: EN_TINY, young: EN_YOUNG, middle: EN_MIDDLE },
  sl: { tiny: SL_TINY, young: SL_YOUNG, middle: SL_MIDDLE },
  it: { tiny: IT_TINY, young: IT_YOUNG, middle: IT_MIDDLE },
}

export function getRebusPuzzles(
  language: Language,
  ageGroup: AgeGroup,
  count: number,
): StaticRebusPuzzle[] {
  const bank = BANKS[language]?.[ageGroup] ?? EN_YOUNG
  // Shuffle then take `count` (with wrap-around if bank is smaller)
  const shuffled = [...bank].sort(() => Math.random() - 0.5)
  const result: StaticRebusPuzzle[] = []
  while (result.length < count) {
    result.push(...shuffled.slice(0, Math.min(count - result.length, shuffled.length)))
  }
  return result.slice(0, count)
}
